import type { SxProps, Theme } from '@mui/material'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../constants/colors'

const MODAL_MAX_HEIGHT = '85vh'
const DEFAULT_MODAL_PADDING_SPACING = 4 // 32px of DefaultModal padding, cancelled so header and footer run edge to edge
const CLOSE_BUTTON_CLEARANCE_SPACING = 8 // 64px, keeps the header clear of the modal's own close button
const PROGRESS_BAR_HEIGHT_PX = 3
const WORDMARK_HEIGHT_PX = 16
const WORDMARK_OPACITY = 0.7

export const PRIVACY_NOTICE_MODAL_MAX_WIDTH = '800px'

const modalPaddingX = { xs: 2.5, sm: 4, md: 4 }

const bodyTextSx = {
  color: INTERAX_COLORS.typographyFont,
  fontFamily: INTERAX_TYPOGRAPHY.body2.fontFamily,
  fontWeight: INTERAX_TYPOGRAPHY.body2.fontWeight,
  fontSize: INTERAX_TYPOGRAPHY.body2.fontSize,
  lineHeight: INTERAX_TYPOGRAPHY.body2.lineHeight
}

export const modalLayoutSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  maxHeight: MODAL_MAX_HEIGHT,
  m: -DEFAULT_MODAL_PADDING_SPACING
}

export const modalHeaderSx: SxProps<Theme> = {
  flexShrink: 0,
  pl: modalPaddingX,
  pr: CLOSE_BUTTON_CLEARANCE_SPACING,
  pt: 3,
  pb: 2.5
}

export const modalWordmarkSx: SxProps<Theme> = {
  display: 'block',
  width: 'auto',
  height: WORDMARK_HEIGHT_PX,
  opacity: WORDMARK_OPACITY,
  mb: 1.5
}

export const modalTitleSx: SxProps<Theme> = {
  m: 0,
  color: INTERAX_COLORS.white,
  fontFamily: INTERAX_TYPOGRAPHY.headline4.fontFamily,
  fontWeight: INTERAX_TYPOGRAPHY.headline4.fontWeight,
  fontSize: {
    xs: INTERAX_TYPOGRAPHY.headline5.fontSize,
    sm: INTERAX_TYPOGRAPHY.headline4.fontSize,
    md: INTERAX_TYPOGRAPHY.headline4.fontSize
  },
  lineHeight: {
    xs: INTERAX_TYPOGRAPHY.headline5.lineHeight,
    sm: INTERAX_TYPOGRAPHY.headline4.lineHeight,
    md: INTERAX_TYPOGRAPHY.headline4.lineHeight
  },
  overflowWrap: 'break-word',
  hyphens: 'auto'
}

export const progressTrackSx: SxProps<Theme> = {
  flexShrink: 0,
  height: PROGRESS_BAR_HEIGHT_PX,
  backgroundColor: INTERAX_COLORS.white10
}

export const progressFillSx = (progressPercent: number): SxProps<Theme> => ({
  width: `${progressPercent}%`,
  height: '100%',
  backgroundColor: INTERAX_COLORS.sun,
  transition: 'width 0.1s linear'
})

export const modalBodySx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  px: modalPaddingX,
  py: 3,
  '&::-webkit-scrollbar': { width: 4 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { background: INTERAX_COLORS.sun, borderRadius: 2 }
}

export const modalFooterSx: SxProps<Theme> = {
  flexShrink: 0,
  px: modalPaddingX,
  py: { xs: 2, sm: 3, md: 3 },
  borderTop: `1px solid ${INTERAX_COLORS.white10}`,
  display: 'flex',
  flexDirection: { xs: 'column-reverse', sm: 'row', md: 'row' },
  justifyContent: 'flex-end',
  gap: { xs: 1.5, sm: 2, md: 2 }
}

export const modalFooterButtonSx: SxProps<Theme> = {
  width: { xs: '100%', sm: 'auto', md: 'auto' }
}

export const lastUpdatedSx: SxProps<Theme> = {
  color: INTERAX_COLORS.white70,
  fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
  fontWeight: INTERAX_TYPOGRAPHY.body3.fontWeight,
  fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
  lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight,
  fontStyle: 'italic',
  mb: 2
}

export const sectionSx: SxProps<Theme> = {
  mt: 4
}

export const sectionTitleSx: SxProps<Theme> = {
  m: 0,
  mb: 1.5,
  color: INTERAX_COLORS.white,
  fontFamily: INTERAX_TYPOGRAPHY.headline5.fontFamily,
  fontWeight: INTERAX_TYPOGRAPHY.headline5.fontWeight,
  fontSize: INTERAX_TYPOGRAPHY.headline5.fontSize,
  lineHeight: INTERAX_TYPOGRAPHY.headline5.lineHeight
}

export const subheadingSx: SxProps<Theme> = {
  m: 0,
  mt: 2.5,
  mb: 1,
  color: INTERAX_COLORS.white,
  fontFamily: INTERAX_TYPOGRAPHY.headline6.fontFamily,
  fontWeight: INTERAX_TYPOGRAPHY.headline6.fontWeight,
  fontSize: INTERAX_TYPOGRAPHY.headline6.fontSize,
  lineHeight: INTERAX_TYPOGRAPHY.headline6.lineHeight
}

export const paragraphSx: SxProps<Theme> = {
  ...bodyTextSx,
  mb: 1.5
}

export const listSx: SxProps<Theme> = {
  m: 0,
  mb: 1.5,
  pl: 3,
  listStyleType: 'disc',
  '& li::marker': { color: INTERAX_COLORS.sun }
}

export const listItemSx: SxProps<Theme> = {
  ...bodyTextSx,
  mb: 0.75
}

export const emphasisSx: SxProps<Theme> = {
  color: INTERAX_COLORS.white,
  fontWeight: 600
}

export const contactLinkSx: SxProps<Theme> = {
  ...bodyTextSx,
  display: 'inline-block',
  mb: 1.5,
  color: INTERAX_COLORS.sun,
  fontWeight: 600,
  textDecorationColor: INTERAX_COLORS.sun,
  overflowWrap: 'anywhere'
}
